// Copyright 2025, University of Maryland, All Rights Reserved
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Plus, Trash2 } from "lucide-react"
import React, { useState, useRef, useEffect } from "react"
import type { SchemaField, SchemaFieldConfig } from "@/types/schema-fields"
import { v4 as uuidv4 } from "uuid"
import { BaseField, type BaseFieldEditorProps } from "./BaseField.tsx"
import { IntegerFieldEditor } from "./IntegerField.tsx"
import { EnumFieldEditor } from "./EnumField.tsx"
import { ScoreFieldEditor } from "./ScoreField.tsx"

export const ObjectArrayFieldEditor = React.memo(({
  field,
  onFieldUpdate,
  ...restProps
}: BaseFieldEditorProps) => {
  const config = field.config
  const is_loading = restProps.isLoading

  const update_config = (config_updates: Record<string, any>) => {
    onFieldUpdate(field, {
      config: { ...config, ...config_updates }
    })
  }

  // Initialize properties from config or use defaults
  const initial_properties = config.properties || {
    name: {
      type: 'string',
      title: 'Name',
      description: 'Property name',
      required: true
    },
    description: {
      type: 'string', 
      title: 'Description',
      description: 'Property description',
      required: false
    }
  }

  const [properties, setProperties] = useState<Record<string, SchemaFieldConfig>>(initial_properties)

  // Map property keys to stable UUIDs for React keys
  const property_keys_to_uuids = useRef<Record<string, string>>({})

  // Initialize UUIDs for all properties
  useEffect(() => {
    Object.keys(properties).forEach(key => {
      if (!property_keys_to_uuids.current[key]) {
        property_keys_to_uuids.current[key] = uuidv4()
      }
    })

    // Clean up UUIDs for deleted properties
    Object.keys(property_keys_to_uuids.current).forEach(key => {
      if (!properties[key]) {
        delete property_keys_to_uuids.current[key]
      }
    })
  }, [properties])

  // Sync properties state with config updates when they change externally
  useEffect(() => {
    if (config.properties) {
      const config_props_str = JSON.stringify(config.properties)
      const state_props_str = JSON.stringify(properties)

      if (config_props_str !== state_props_str) {
        setProperties(config.properties)

        // Reset UUID mappings for new properties
        property_keys_to_uuids.current = {}
        Object.keys(config.properties).forEach(key => {
          property_keys_to_uuids.current[key] = uuidv4()
        })
      }
    }
  }, [config.properties]) // Only depend on config.properties, not properties state

  const add_property = () => {
    const property_key = `property_${Date.now()}`
    const new_properties = {
      ...properties,
      [property_key]: {
        type: 'string' as const,
        title: 'New Property',
        description: 'Property description',
        required: false
      }
    }

    // Generate UUID for new property
    property_keys_to_uuids.current[property_key] = uuidv4()

    setProperties(new_properties)
    update_config({ properties: new_properties })
  }

  const remove_property = (property_key: string) => {
    const new_properties = { ...properties }
    delete new_properties[property_key]

    // Clean up UUID mapping
    delete property_keys_to_uuids.current[property_key]

    setProperties(new_properties)
    update_config({ properties: new_properties })
  }

  const update_property = (property_key: string, updates: Partial<SchemaFieldConfig>) => {
    const new_properties = {
      ...properties,
      [property_key]: { ...properties[property_key], ...updates }
    }
    setProperties(new_properties)
    update_config({ properties: new_properties })
  }

  const update_property_key = (old_key: string, new_key: string) => {
    if (old_key === new_key || !new_key.trim()) return

    const new_properties = { ...properties }
    const property_config = new_properties[old_key]
    delete new_properties[old_key]
    new_properties[new_key] = property_config

    // Update UUID mapping
    property_keys_to_uuids.current[new_key] = property_keys_to_uuids.current[old_key]
    delete property_keys_to_uuids.current[old_key]

    setProperties(new_properties)
    update_config({ properties: new_properties })
  }

  const get_field_editor_component = (property_config: SchemaFieldConfig) => {
    switch (property_config.type) {
      case 'integer':
        return IntegerFieldEditor
      case 'enum_field':
        return EnumFieldEditor
      case 'score':
        return ScoreFieldEditor
      case 'string':
      default:
        return BaseField
    }
  }

  const get_default_config = (type: string): Partial<SchemaFieldConfig> => {
    const new_config: Partial<SchemaFieldConfig> = { type: type as any }
    
    if (type === 'enum_field') {
      new_config.enum_options = ['option1', 'option2', 'option3']
    } else if (type === 'integer') {
      new_config.min = 0
      new_config.max = 100
    } else if (type === 'score') {
      new_config.score_min = 0
      new_config.score_max = 10
      new_config.type_options = ['positive', 'neutral', 'negative']
    }
    
    return new_config
  }

  const handle_type_change = (property_key: string, new_type: string) => {
    const default_config = get_default_config(new_type)
    const current_config = properties[property_key]
    
    update_property(property_key, {
      ...current_config,
      ...default_config,
      type: new_type as any
    })
  }

  return (
    <BaseField field={field} onFieldUpdate={onFieldUpdate} {...restProps}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Object Properties</Label>
          <Button
            variant="outline"
            size="sm"
            onClick={add_property}
            disabled={is_loading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Property
          </Button>
        </div>

        {Object.keys(properties).length === 0 ? (
          <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-lg">
            <p className="text-sm">No properties configured</p>
            <p className="text-xs">Add properties to define the object structure</p>
          </div>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {Object.entries(properties).map(([property_key, property_config]) => {
              const FieldEditor = get_field_editor_component(property_config)
              
              // Create virtual field for the property
              const virtual_field: SchemaField = {
                uuid: property_keys_to_uuids.current[property_key],
                id: property_key,
                title: property_config.title,
                description: property_config.description,
                field_type: property_config.type,
                config: property_config,
                order: 0
              }

              const handle_property_update = (_: SchemaField, updates: Partial<SchemaField>) => {
                const config_updates: Partial<SchemaFieldConfig> = {}
                
                if (updates.title !== undefined) {
                  config_updates.title = updates.title
                }
                if (updates.description !== undefined) {
                  config_updates.description = updates.description
                }
                if (updates.config) {
                  Object.assign(config_updates, updates.config)
                }
                
                update_property(property_key, config_updates)
              }

              return (
                <div key={property_keys_to_uuids.current[property_key]} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="space-y-1">
                        <Label className="text-xs">Property Key</Label>
                        <Input
                          value={property_key}
                          onChange={(e) => update_property_key(property_key, e.target.value)}
                          placeholder="property_key"
                          className="font-mono text-sm h-8 min-w-32"
                          disabled={is_loading}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Type</Label>
                        <Select
                          value={property_config.type}
                          onValueChange={(value) => handle_type_change(property_key, value)}
                          disabled={is_loading}
                        >
                          <SelectTrigger className="text-sm h-8 min-w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="string">String</SelectItem>
                            <SelectItem value="integer">Integer</SelectItem>
                            <SelectItem value="enum_field">Enum</SelectItem>
                            <SelectItem value="score">Score</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => remove_property(property_key)}
                      disabled={is_loading}
                      title="Remove property"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  
                  <FieldEditor
                    field={virtual_field}
                    onFieldUpdate={handle_property_update}
                    onRemove={() => remove_property(property_key)}
                    isLoading={is_loading}
                    readonly_id={true}
                    non_deletable={true}
                  />
                </div>
              )
            })}
          </div>
        )}
        
        <p className="text-xs text-muted-foreground">
          Define the structure of objects in this array. Each object will have these properties with their specific configurations.
        </p>
      </div>
    </BaseField>
  )
})

ObjectArrayFieldEditor.displayName = 'ObjectArrayFieldEditor'
